import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toastHelpers } from '@/lib/toast-helpers'
import type { CounterServer, Order } from '@/types'
import { ChevronDown, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

export function counterServerLabel(s: CounterServer): string {
  return `${s.first_name} ${s.last_name}`
}

export function counterServerLabelFull(s: CounterServer): string {
  return `${s.first_name} ${s.last_name} (@${s.username})`
}

export type CounterTableServiceBodyProps = {
  existingOrder: Order | null
  onServiceUpdated: (order: Order) => void
}

/**
 * Headless body for the Party + Server section. Rendered inside a popover
 * by `CounterRailToolbar`; the trigger and "is meaningful" gate live on the
 * toolbar (this body assumes there is an `existingOrder` to save against).
 */
export function CounterTableServiceBody({
  existingOrder,
  onServiceUpdated,
}: CounterTableServiceBodyProps) {
  const queryClient = useQueryClient()
  const [partyDigits, setPartyDigits] = useState('')
  const [serverMenuOpen, setServerMenuOpen] = useState(false)
  const [serverFilter, setServerFilter] = useState('')
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null)
  const [tableNotes, setTableNotes] = useState('')

  const { data: serversRes, isError: serversQueryIsError } = useQuery({
    queryKey: ['counterServers'],
    queryFn: () => apiClient.getCounterServers(),
    enabled: Boolean(existingOrder?.id),
  })

  const allServers = serversRes?.success && serversRes.data ? serversRes.data : []

  const filteredServers = useMemo(() => {
    const q = serverFilter.trim().toLowerCase()
    if (!q) return allServers
    return allServers.filter((s) => {
      const full = `${s.first_name} ${s.last_name}`.toLowerCase()
      return (
        s.username.toLowerCase().includes(q) ||
        s.first_name.toLowerCase().includes(q) ||
        s.last_name.toLowerCase().includes(q) ||
        full.includes(q)
      )
    })
  }, [allServers, serverFilter])

  const selectedServer = useMemo(
    () => allServers.find((s) => s.id === selectedServerId) ?? null,
    [allServers, selectedServerId]
  )

  useEffect(() => {
    if (!existingOrder?.id) return
    const gc = existingOrder.guest_count ?? 0
    setPartyDigits(gc > 0 ? String(gc) : '')
    setSelectedServerId(existingOrder.user_id ?? null)
    setTableNotes(existingOrder.notes ?? '')
    setServerFilter('')
    setServerMenuOpen(false)
  }, [existingOrder?.id, existingOrder?.guest_count, existingOrder?.user_id, existingOrder?.notes])

  const onPartyInput = useCallback((raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 3)
    setPartyDigits(digits)
  }, [])

  const partyCount = partyDigits === '' ? 0 : Math.max(0, parseInt(partyDigits, 10) || 0)

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!existingOrder?.id) throw new Error('No order')
      const res = await apiClient.updateCounterOrderService(existingOrder.id, {
        guest_count: partyCount,
        assigned_server_id: selectedServerId ?? '',
        notes: tableNotes.trim(),
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not save table service')
      }
      return res.data
    },
    onSuccess: (order) => {
      onServiceUpdated(order)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      toastHelpers.success('Table service saved', 'Party size and server updated on this order.')
    },
    onError: (e: Error) => {
      toastHelpers.error('Table service', e.message || 'Save failed')
    },
  })

  const listEmptyMessage = (() => {
    if (serversQueryIsError) return 'Could not load staff.'
    if (allServers.length === 0) return 'No servers found'
    if (filteredServers.length === 0) return 'No matches — try another search'
    return null
  })()

  if (!existingOrder?.id) {
    return (
      <p className="text-sm text-muted-foreground">
        Open a table session before assigning party size or server.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-snug text-muted-foreground">
        Saved with this order. You can edit until checkout closes.
      </p>
      {/* One field per row. These were side-by-side at sm+, which left the
          server trigger — and the search box in its dropdown, whose width
          tracks the trigger — too narrow to read a full name in. */}
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-foreground">Number of guests</Label>
          <Input
            inputMode="numeric"
            autoComplete="off"
            className="h-9 text-sm tabular-nums placeholder:text-xs placeholder:text-muted-foreground/60"
            value={partyDigits}
            onChange={(e) => onPartyInput(e.target.value)}
            placeholder="Not set"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-foreground">Server / waiter</Label>
          <DropdownMenu open={serverMenuOpen} onOpenChange={setServerMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={allServers.length === 0 || serversQueryIsError}
                className="h-9 w-full justify-between text-sm font-normal"
              >
                <span
                  className={cn(
                    'truncate text-sm',
                    selectedServer ? 'text-foreground' : 'text-xs text-muted-foreground/60'
                  )}
                >
                  {selectedServer ? counterServerLabel(selectedServer) : 'Not assigned'}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
              align="start"
            >
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className="h-8 pl-8 text-sm placeholder:text-xs placeholder:text-muted-foreground/60"
                    placeholder="Search by name…"
                    value={serverFilter}
                    onChange={(e) => setServerFilter(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <button
                type="button"
                className="w-full border-b border-border px-3 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted/80"
                onClick={() => {
                  setSelectedServerId(null)
                  setServerMenuOpen(false)
                  setServerFilter('')
                }}
              >
                Clear server
              </button>
              <div className="max-h-40 overflow-y-auto py-0.5">
                {listEmptyMessage ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                    {listEmptyMessage}
                  </div>
                ) : (
                  filteredServers.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm hover:bg-muted/80 transition-colors',
                        selectedServerId === s.id && 'bg-primary/10'
                      )}
                      onClick={() => {
                        setSelectedServerId(s.id)
                        setServerMenuOpen(false)
                        setServerFilter('')
                      }}
                    >
                      <span className="font-medium text-sm">
                        {s.first_name} {s.last_name}
                      </span>
                      <span className="text-muted-foreground/70 ml-1 text-xs">@{s.username}</span>
                    </button>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Table notes</Label>
        <Textarea
          className="min-h-[56px] resize-none text-sm placeholder:text-xs placeholder:text-muted-foreground/60"
          value={tableNotes}
          onChange={(e) => setTableNotes(e.target.value)}
          placeholder="Allergies, birthday, VIP, special requests…"
          maxLength={500}
        />
      </div>
      {existingOrder && (
        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save to order'}
        </Button>
      )}
    </div>
  )
}
